/**
 * Row identity for the pricing grids' unsaved-edit tracking.
 *
 * A grid's composite key (BrokersGrid BROKER|PRICE_FILE, PricingGrid ID2|BROKER,
 * PricingFrequency ID2) names the row the writer will save, and the user can
 * type any value into it, including one another row already has. So a row is
 * tracked, and identified to AG Grid, by a key that typing cannot make collide:
 *
 * - a new row by its TEMP_ROW_ID;
 * - a loaded row whose key was edited by the KEY_EDIT_ID it gets on that first
 *   key edit (and keeps until the next full reload);
 * - any other loaded row by its composite key, which the server keeps unique.
 *
 * Two rows that end up sharing a composite key are therefore still two rows,
 * each with its own tracked edits. findDuplicateKeys reports them so Save can
 * be blocked until the user resolves the collision.
 */
import { loadedModifiedDate } from '@1823-partners/core';

/** Client-only field; it is stripped from every save and delete payload. */
export const KEY_EDIT_ID_FIELD = '_KEY_EDIT_ID';

let keyEditCounter = 0;

/** A tracking key for a loaded row whose key has been edited, unique for the session. */
export const nextKeyEditId = () => {
  keyEditCounter += 1;
  return `key_edit_${keyEditCounter}`;
};

/** `row`'s tracking key (and AG Grid row id), given the grid's composite key. */
export const makeTrackingKeyOf = (compositeKeyOf) => (row) =>
  row.TEMP_ROW_ID || row[KEY_EDIT_ID_FIELD] || compositeKeyOf(row);

/**
 * Gives a loaded row its KEY_EDIT_ID on its first key edit. It is written onto
 * the row in place, as AG Grid writes the edit itself, so a second edit in the
 * same tick (a paste) already resolves to the same tracking key.
 */
export const assignKeyEditId = (row) => {
  if (!row.TEMP_ROW_ID && !row[KEY_EDIT_ID_FIELD]) {
    row[KEY_EDIT_ID_FIELD] = nextKeyEditId();
  }
  return row;
};

/** A copy of `row` without client-only fields (`_`-prefixed, `actions`, TEMP_ROW_ID). */
export const withoutClientFields = (row) =>
  Object.fromEntries(
    Object.entries(row).filter(([field]) => !field.startsWith('_') && field !== 'actions' && field !== 'TEMP_ROW_ID')
  );

/**
 * A loaded row whose key was edited and then edited back: it carries its
 * loaded MODIFIED_DATE again (keyEditModifiedDate), so it is that server row.
 */
const isBackOnOwnKey = (row) => Boolean(row[KEY_EDIT_ID_FIELD]) && loadedModifiedDate(row) !== null;

/**
 * Composite keys held by more than one row anywhere in the grid, where at
 * least one of those rows would be saved (`isPending`); the others may be
 * unchanged or new. `keyOf` returns null for a row whose key is incomplete
 * (required-field validation reports those). `isCandidate` leaves out rows
 * that are not real rows (PricingGrid's broker placeholders) unless pending.
 * An unchanged row next to a pending row back on its own key is the copy of
 * it a refresh showed while the key was elsewhere (the next refresh folds it
 * in, see mergeUnsavedEdits), not a second row.
 *
 * Returns [{ key, rows }] in grid order.
 */
export const findDuplicateKeys = ({ rows, keyOf, isPending, isCandidate = () => true }) => {
  const groups = new Map();
  rows.forEach((row) => {
    if (!isCandidate(row) && !isPending(row)) return;
    const key = keyOf(row);
    if (key === null || key === undefined) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return [...groups.entries()]
    .filter(([, group]) => {
      const pending = group.filter(isPending);
      const counted = pending.some(isBackOnOwnKey) ? pending : group;
      return pending.length > 0 && counted.length > 1;
    })
    .map(([key, group]) => ({ key, rows: group }));
};

/**
 * The banner for a Save blocked by duplicate keys. `keyLabel` names the key
 * (e.g. "Broker + Price File"); `describe(row)` renders a row's key value.
 */
export const duplicateKeysMessage = (duplicates, { keyLabel, describe }) => {
  const list = duplicates.map(({ rows }) => `${describe(rows[0])} (${rows.length} rows)`).join('; ');
  const which = duplicates.length === 1 ? 'this one is' : 'these are';
  return `Nothing was saved. Each ${keyLabel} must be on only one row, and ${which} on more than one: ${list}. `
    + 'Change or delete the extra rows, then save again.';
};
